import React, { useState } from "react";
import Modal from "../../UIElements/Modal";
import { Input } from "../../UIElements/Input";
import { Button } from "../../UIElements/Button";
import { Icon } from "../../UIElements/Icon";
import { PhoneInputSimple } from "../../UIElements/PhoneInput/PhoneInputSimple";
import { useCreateUser } from "../../../../hooks/useCreateUser";

const CreateUserModal = ({ isOpen, onClose }) => {
  const createUserMutation = useCreateUser();
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    phone: "", // Full phone number with country code
    password: "",
    photo: null,
  });
  const [phoneData, setPhoneData] = useState({
    countryCode: "",
    phoneNumber: "",
    fullNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handlePhoneDataChange = (data) => {
    setPhoneData(data);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          photo: "Please select a valid image file",
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          photo: "Image size must be less than 5MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setFormData((prev) => ({
          ...prev,
          photo: base64,
        }));
        setImagePreview(base64);
        setErrors((prev) => ({
          ...prev,
          photo: null,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+\d{10,15}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number with country code";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Use phone data that's already separated by PhoneInputSimple
      const countryCode = phoneData.countryCode || "58"; // Default to Venezuela
      const cellphone = phoneData.phoneNumber || formData.phone;

      const userData = {
        level: 0, // Fixed to 0 for Admins as requested
        name: formData.name.trim(),
        lastname: formData.lastname.trim(),
        email: formData.email.trim(),
        country_phone_code: countryCode,
        cellphone: cellphone,
        password: formData.password,
      };
      // Only include photo if one was selected
      if (formData.photo) {
        userData.photo = formData.photo;
      }

      await createUserMutation.mutateAsync(userData);

      // Reset form and close modal on success
      handleClose();
    } catch (error) {
      // Error is handled by the mutation hook
      console.error("Create user error:", error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      lastname: "",
      email: "",
      phone: "",
      password: "",
      photo: null,
    });
    setPhoneData({
      countryCode: "",
      phoneNumber: "",
      fullNumber: "",
    });
    setErrors({});
    setImagePreview(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New User"
      subtitle="Add a new admin user to the system"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo and Name Fields */}
        <div className="flex items-end space-x-6">
          {/* Profile Photo - 40% width */}
          <div className="w-2/5 max-w-40">
            <div className="space-y-3">
              <div className="flex justify-center">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      className="border-border h-24 w-24 rounded-full border-2 object-cover"
                      src={imagePreview}
                      alt="Profile preview"
                    />
                    {/* Overlay - Dots and Image Icon - Always visible */}
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                      <div className="flex items-center space-x-1">
                        <Icon
                          name="Image"
                          className="text-primary-foreground h-5 w-5"
                        />
                        <Icon
                          name="MoreVertical"
                          className="text-primary-foreground h-5 w-5"
                        />
                      </div>
                    </div>
                    {/* Clickable overlay */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                ) : (
                  <div className="bg-muted flex h-24 w-24 items-center justify-center rounded-full">
                    <Icon
                      name="User"
                      className="text-muted-foreground h-10 w-10"
                    />
                  </div>
                )}
              </div>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="border-border bg-muted text-foreground hover:bg-accent focus-visible:ring-ring block w-full cursor-pointer rounded-md border px-3 py-2 text-center text-[length:var(--font-size-product-ui)] transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                  {imagePreview ? "Change Photo" : "Select Photo"}
                </label>
              </div>
              {errors.photo && (
                <p className="text-error text-center text-xs">{errors.photo}</p>
              )}
            </div>
          </div>

          {/* Name Fields - 60% width */}
          <div className="flex-1 space-y-4">
            <Input
              label="First Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={errors.name}
              variant="dark"
              placeholder="Enter first name"
              required
            />
            <Input
              label="Last Name"
              value={formData.lastname}
              onChange={(e) => handleInputChange("lastname", e.target.value)}
              error={errors.lastname}
              variant="dark"
              placeholder="Enter last name"
              required
            />
          </div>
        </div>

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          error={errors.email}
          placeholder="Enter email address"
          required
        />

        {/* Phone Field */}
        <PhoneInputSimple
          label="Phone Number"
          value={formData.phone}
          onChange={(value) => handleInputChange("phone", value || "")}
          onPhoneDataChange={handlePhoneDataChange}
          error={errors.phone}
          placeholder="Enter phone number"
          size="md"
        />

        {/* Password */}
        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          error={errors.password}
          placeholder="Enter password (min 8 characters)"
          required
        />

        {/* User Type Info */}
        <div className="border-primary/20 bg-accent rounded-md border p-4">
          <div className="flex items-center">
            <Icon name="Shield" className="text-primary mr-2 h-5 w-5" />
            <div>
              <h4 className="text-foreground text-sm font-medium">User Type</h4>
              <p className="text-foreground mt-1 text-sm">
                This user will be created as an Administrator
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row">
          <Button
            type="button"
            emphasis="outline"
            intent="neutral"
            onClick={handleClose}
            className="flex-1 sm:flex-none"
            disabled={createUserMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={createUserMutation.isPending}
            className="w-full sm:w-auto"
            disabled={createUserMutation.isPending}
          >
            {createUserMutation.isPending ? "Creating User..." : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserModal;
